// Determine the API endpoint based on environment
const getApiEndpoint = () => {
  // In production, you'll need to update this to your deployed backend URL
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
  return `${backendUrl}/api/claude`;
};

// Direct Claude API call (only use in secure environments like GitHub Actions)
const callClaudeDirectly = async (question, accountData) => {
  const systemPrompt = `You are a Customer Success Manager assistant analyzing customer account data. 
You have access to the following customer accounts data:
${JSON.stringify(accountData, null, 2)}

Provide helpful, specific insights about the accounts based on the user's question. 
Focus on health scores, recent tickets, usage trends, and actionable recommendations.
Keep responses concise and professional.`;

  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': process.env.REACT_APP_CLAUDE_API_KEY,
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 500,
        messages: [
          {
            role: 'user',
            content: `${systemPrompt}\n\nUser question: ${question}`
          }
        ]
      })
    });

    if (!response.ok) {
      console.error('Claude API direct call failed');
      return getMockResponse(question, accountData);
    }

    const data = await response.json();
    return data.content[0].text;
  } catch (error) {
    console.error('Direct Claude API error:', error);
    return getMockResponse(question, accountData);
  }
};

// Mock response for when API is not available
const getMockResponse = (question, accountData) => {
  // Get actual account names from the data
  const accounts = accountData ? Object.values(accountData).slice(0, 3) : [];
  const accountNames = accounts.map(a => a.name).join(', ');
  
  const responses = {
    health: `Based on the account data, I can see several accounts with varying health scores. Accounts like ${accountNames} show different levels of engagement. Review each account's metrics for specific insights.`,
    risk: "Looking at the health scores and validation trends, accounts with declining monthly projections may need attention. Check the Volume Trend indicators for accounts showing decline.",
    usage: "Usage patterns vary across accounts. Review the Data Assure validation charts to identify accounts with strong growth versus those with declining activity.",
    default: "I'm analyzing your customer data. Please check the dashboard for specific account metrics, validation trends, and health scores."
  };
  
  // Simple keyword matching for demo
  if (question.toLowerCase().includes('health')) return responses.health;
  if (question.toLowerCase().includes('risk')) return responses.risk;
  if (question.toLowerCase().includes('usage')) return responses.usage;
  return responses.default;
};

export const askClaude = async (question, accountData) => {
  console.log('Claude API called with question:', question);
  
  // Check if we should use mock mode (for demo/production without backend)
  if (process.env.REACT_APP_USE_MOCK_API === 'true') {
    console.log('Using mock API response');
    return getMockResponse(question, accountData);
  }
  
  // If we have a direct API key and no backend URL, use Claude API directly
  if (process.env.REACT_APP_CLAUDE_API_KEY && !process.env.REACT_APP_BACKEND_URL) {
    return callClaudeDirectly(question, accountData);
  }
  
  try {
    console.log('Sending request to backend proxy...');
    const response = await fetch(getApiEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        question,
        accountData
      })
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('API error response:', errorData);
      if (response.status === 400 && errorData.error.includes('API key')) {
        // Fall back to mock response if API key is not configured
        console.log('API key not configured, using mock response');
        return getMockResponse(question, accountData);
      }
      throw new Error(`API request failed: ${errorData.error}`);
    }

    const data = await response.json();
    console.log('Claude response received:', data);
    return data.response;
  } catch (error) {
    console.error('Claude API error details:', error);
    // If backend is not available, use mock response
    if (error.message.includes('fetch failed') || error.message.includes('NetworkError')) {
      console.log('Backend not available, using mock response');
      return getMockResponse(question, accountData);
    }
    throw error;
  }
};